// apps/web/src/pages/author/constraint-config/PredicateNode.tsx
//
// Phase 9 Plan 08 Task 1 — recursive PredicateNode editor for the bounded
// Predicate AST (D-20 5th constraint kind).
//
// Reference: 09-UI-SPEC §"Predicate AST editor — bounded recursive node spec"
// (lines 597-654). The Predicate AST schema lives in
// packages/shared/src/schemas/constraint.ts (lines 75-141): three leaf families
// (boolean / numeric / string) + and/or groups; depth ≤ 8, breadth ≤ 10,
// total nodes ≤ 100 enforced by the schema; the UI bounds counter +
// disabled "+ Add condition" button at breadth=10 are the load-bearing
// affordances that prevent authoring over-cap predicates.
//
// React.memo on the function (UI-SPEC Performance Contract #2) prevents
// re-render thrash on every keystroke — each leaf's only subscription is its
// own useWatch path, so unrelated edits do not re-render this node.
//
// Inline style={{ marginLeft: `${depth * 4}px` }} is the documented Phase 9
// CARVE-OUT — Plan 09-13 extends the grep gate's allowlist to permit this
// single inline style. See 09-UI-SPEC §"Predicate AST editor" line 656-659.
//
// authr-06 hygiene: per-path useWatch ONLY. NEVER top-level. NEVER reads
// formState.* (would re-render on every keystroke anywhere in the form).
//
// authr-03 hygiene: useFieldArray inside GroupPredicateEditor uses `field.id`
// (NEVER index) as the React key — Plan 01 grep gate enforces.
//
// convertNodeKind: handles transitions between kinds. and ↔ or preserves
// predicates; leaf → and/or creates a single default leaf child; and/or → leaf
// with non-empty predicates opens a destructive confirm Dialog (children
// discarded on user confirm).

import type { Predicate } from "@the-recipe-spread/shared";
import { X } from "lucide-react";
import * as React from "react";
import {
  type Control,
  type FieldValues,
  useFieldArray,
  useFormContext,
  type UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { computePredicateStats } from "./PredicateBoundsCounter.js";

// Per the schema (constraint.ts lines 80-105), variables map to families:
//   boolean: { batch-set-empty, other-batch-sets-all-empty } × { is-true, is-false }, no value
//   numeric: { batch-set-position, spread-recipe-count } × { eq, ne, lt, lte, gt, gte }, value: number
//   string:  { active-mode-id }                            × { eq, ne },                value: string
const BOOLEAN_VARIABLES = ["batch-set-empty", "other-batch-sets-all-empty"] as const;
const NUMERIC_VARIABLES = ["batch-set-position", "spread-recipe-count"] as const;
const STRING_VARIABLES = ["active-mode-id"] as const;
const ALL_VARIABLES = [
  ...BOOLEAN_VARIABLES,
  ...NUMERIC_VARIABLES,
  ...STRING_VARIABLES,
] as const;

type LeafVariable = (typeof ALL_VARIABLES)[number];
type Family = "boolean" | "numeric" | "string";

const BOOLEAN_OPERATORS = ["is-true", "is-false"] as const;
const NUMERIC_OPERATORS = ["eq", "ne", "lt", "lte", "gt", "gte"] as const;
const STRING_OPERATORS = ["eq", "ne"] as const;

function familyOf(variable: string): Family {
  if ((BOOLEAN_VARIABLES as readonly string[]).includes(variable)) return "boolean";
  if ((NUMERIC_VARIABLES as readonly string[]).includes(variable)) return "numeric";
  return "string"; // active-mode-id (and any unknown — defensive)
}

function defaultLeaf(): Predicate {
  return { kind: "leaf", variable: "batch-set-empty", operator: "is-true" };
}

// Default value for the leaf based on the family — used when the user changes
// variable across families (we reset operator + value to consistent defaults).
function defaultLeafForVariable(variable: LeafVariable): Predicate {
  const fam = familyOf(variable);
  if (fam === "boolean") {
    return { kind: "leaf", variable: variable as "batch-set-empty" | "other-batch-sets-all-empty", operator: "is-true" };
  }
  if (fam === "numeric") {
    return {
      kind: "leaf",
      variable: variable as "batch-set-position" | "spread-recipe-count",
      operator: "eq",
      value: 0,
    };
  }
  return { kind: "leaf", variable: "active-mode-id", operator: "eq", value: "" };
}

// convertNodeKind — non-Dialog branches (and ↔ or, leaf → and/or). The
// destructive group → leaf branch is handled by the host PredicateNode via
// React state + Dialog. See the inline call sites.
function convertNodeKindInline(
  setValue: UseFormSetValue<Record<string, unknown>>,
  path: string,
  currentNode: Predicate | undefined,
  newKind: "leaf" | "and" | "or",
): void {
  const currentKind = currentNode?.kind ?? "leaf";
  if (currentKind === newKind) return;

  // and ↔ or — preserve predicates verbatim.
  if (
    (currentKind === "and" || currentKind === "or") &&
    (newKind === "and" || newKind === "or")
  ) {
    const preserved: Predicate = {
      kind: newKind,
      predicates: (currentNode as { predicates: readonly Predicate[] }).predicates,
    };
    setValue(path, preserved, { shouldDirty: true, shouldTouch: true });
    return;
  }

  // leaf → and/or — seed with one default leaf child so the group has the
  // minimum valid predicates.length=1 per schema breadth.min(1).
  if (currentKind === "leaf" && (newKind === "and" || newKind === "or")) {
    const seeded: Predicate = {
      kind: newKind,
      predicates: [defaultLeaf()],
    };
    setValue(path, seeded, { shouldDirty: true, shouldTouch: true });
    return;
  }
}

export interface PredicateNodeProps {
  /** RHF form path to this node, e.g. "predicate" or "predicate.predicates.0". */
  path: string;
  /** Current depth (0 at root); used for indent + bound check by the host. */
  depth: number;
  /** Present on children; absent on the root node. */
  onRemove?: () => void;
}

// React.memo applied per UI-SPEC Performance Contract #2 — prevents re-render
// thrash at the leaf level when sibling subtrees or unrelated form fields edit.
export const PredicateNode = React.memo(function PredicateNode({
  path,
  depth,
  onRemove,
}: PredicateNodeProps): React.ReactElement {
  const { control, setValue } = useFormContext();
  // Per-path useWatch ONLY — never top-level.
  const node = useWatch({ control, name: path }) as Predicate | undefined;
  const kind: "leaf" | "and" | "or" = node?.kind ?? "leaf";

  // Destructive confirm state — only renders the Dialog while pending.
  const [pendingConvertToLeaf, setPendingConvertToLeaf] = React.useState(false);

  const handleKindChange = React.useCallback(
    (newKind: string) => {
      const targetKind = newKind as "leaf" | "and" | "or";
      // Destructive case: group with non-empty predicates → leaf. Open confirm.
      if (
        (kind === "and" || kind === "or") &&
        targetKind === "leaf" &&
        ((node as { predicates?: readonly Predicate[] }).predicates?.length ?? 0) > 0
      ) {
        setPendingConvertToLeaf(true);
        return;
      }
      // Otherwise: inline conversion.
      convertNodeKindInline(
        setValue as unknown as UseFormSetValue<Record<string, unknown>>,
        path,
        node,
        targetKind,
      );
    },
    [kind, node, path, setValue],
  );

  const confirmConvertToLeaf = React.useCallback(() => {
    // Replace the group node with a fresh default leaf — children discarded.
    setValue(path, defaultLeaf(), { shouldDirty: true, shouldTouch: true });
    setPendingConvertToLeaf(false);
  }, [path, setValue]);

  const cancelConvertToLeaf = React.useCallback(() => {
    setPendingConvertToLeaf(false);
  }, []);

  return (
    <div
      className="rounded-md border border-border bg-card p-3"
      // Documented Phase 9 carve-out — depth-based indent. Plan 09-13 extends
      // the grep gate allowlist to include this exact filepath + property.
      style={{ marginLeft: `${depth * 4}px` }}
      data-predicate-depth={depth}
    >
      <div className="flex items-center gap-2 mb-2">
        <Select value={kind} onValueChange={handleKindChange}>
          <SelectTrigger className="w-48" data-testid="predicate-node-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="leaf">Single condition (leaf)</SelectItem>
            <SelectItem value="and">All of (and)</SelectItem>
            <SelectItem value="or">Any of (or)</SelectItem>
          </SelectContent>
        </Select>
        {onRemove !== undefined ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label="Remove condition"
            data-testid="predicate-node-remove"
          >
            <X className="size-3.5" />
          </Button>
        ) : null}
      </div>

      {kind === "leaf" ? (
        <LeafPredicateEditor path={path} />
      ) : (
        <GroupPredicateEditor
          path={path}
          kind={kind as "and" | "or"}
          depth={depth}
          control={control}
        />
      )}

      {/* Destructive confirm: group → leaf with non-empty predicates. */}
      <Dialog
        open={pendingConvertToLeaf}
        onOpenChange={(open) => {
          if (!open) cancelConvertToLeaf();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to single condition?</DialogTitle>
            <DialogDescription>
              This will discard the nested conditions inside this group. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={cancelConvertToLeaf}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirmConvertToLeaf}>
              Discard and convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
PredicateNode.displayName = "PredicateNode";

// =============================================================================
// LeafPredicateEditor
// =============================================================================

interface LeafPredicateEditorProps {
  path: string;
}

function LeafPredicateEditor({ path }: LeafPredicateEditorProps): React.ReactElement {
  const { control, setValue } = useFormContext();
  // Per-path useWatch — read only the variable + operator at this leaf.
  const variable = useWatch({ control, name: `${path}.variable` }) as LeafVariable | undefined;
  const operator = useWatch({ control, name: `${path}.operator` }) as string | undefined;
  const value = useWatch({ control, name: `${path}.value` }) as string | number | undefined;

  const currentVar: LeafVariable = variable ?? "batch-set-empty";
  const family = familyOf(currentVar);

  // When the variable changes families, reset operator + value to family defaults.
  const handleVariableChange = React.useCallback(
    (next: string) => {
      const nextVar = next as LeafVariable;
      const seeded = defaultLeafForVariable(nextVar);
      setValue(path, seeded, { shouldDirty: true, shouldTouch: true });
    },
    [path, setValue],
  );

  const operators: readonly string[] =
    family === "boolean"
      ? BOOLEAN_OPERATORS
      : family === "numeric"
        ? NUMERIC_OPERATORS
        : STRING_OPERATORS;

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="flex flex-col gap-1">
          <label
            className="text-xs font-medium text-muted-foreground"
            htmlFor={`${path}-variable`}
          >
            Variable
          </label>
          <Select value={currentVar} onValueChange={handleVariableChange}>
            <SelectTrigger
              id={`${path}-variable`}
              data-testid="predicate-node-leaf-variable"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALL_VARIABLES.map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-xs font-medium text-muted-foreground"
            htmlFor={`${path}-operator`}
          >
            Operator
          </label>
          <Select
            value={operator ?? operators[0] ?? ""}
            onValueChange={(next) => {
              setValue(`${path}.operator`, next, {
                shouldDirty: true,
                shouldTouch: true,
              });
            }}
          >
            <SelectTrigger
              id={`${path}-operator`}
              data-testid="predicate-node-leaf-operator"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {operators.map((op) => (
                <SelectItem key={op} value={op}>
                  {op}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {family !== "boolean" ? (
          <div className="flex flex-col gap-1">
            <label
              className="text-xs font-medium text-muted-foreground"
              htmlFor={`${path}-value`}
            >
              Value
            </label>
            <Input
              id={`${path}-value`}
              data-testid="predicate-node-leaf-value"
              type={family === "numeric" ? "number" : "text"}
              value={value === undefined || value === null ? "" : String(value)}
              onChange={(e) => {
                const raw = e.target.value;
                if (family === "numeric") {
                  setValue(`${path}.value`, raw === "" ? 0 : Number(raw), {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                } else {
                  setValue(`${path}.value`, raw, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
LeafPredicateEditor.displayName = "LeafPredicateEditor";

// =============================================================================
// GroupPredicateEditor — and / or
// =============================================================================

interface GroupPredicateEditorProps {
  path: string;
  kind: "and" | "or";
  depth: number;
  control: Control<FieldValues>;
}

function GroupPredicateEditor({
  path,
  kind,
  depth,
  control,
}: GroupPredicateEditorProps): React.ReactElement {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${path}.predicates`,
  });

  // Watch the root predicate to compute over-cap bounds for the disable rule.
  // Per-path useWatch on the root predicate (path's first segment).
  const rootPath = path.split(".")[0] ?? "predicate";
  const rootWatched = useWatch({ control, name: rootPath }) as Predicate | undefined;
  const stats = computePredicateStats(rootWatched);

  // breadth bound: this group cannot exceed 10 children.
  const breadthCapHit = fields.length >= 10;
  // global bounds: if depth or node count is already over cap, also disable.
  const overGlobalBound = stats.depth > 8 || stats.nodes >= 100;
  const addDisabled = breadthCapHit || overGlobalBound;

  return (
    <fieldset className="flex flex-col gap-2">
      <legend
        className={cn(
          "px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        )}
      >
        {kind === "and" ? "All of" : "Any of"}
      </legend>
      {fields.map((field, idx) => (
        <PredicateNode
          key={field.id}
          path={`${path}.predicates.${idx}`}
          depth={depth + 1}
          onRemove={() => remove(idx)}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={addDisabled}
        onClick={() => append(defaultLeaf())}
        data-testid="predicate-node-add-condition"
        className="self-start"
      >
        + Add condition
      </Button>
    </fieldset>
  );
}
GroupPredicateEditor.displayName = "GroupPredicateEditor";
